/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MERGE_SAVE_SIGNATURE_INTO_SAVE_NEXT
 * Date:         2026-02-10
 * Modified By:  Claude Code
 *
 * Description:
 * Removed isSavingSignature and signatureSaved from return (no longer
 * needed after removing separate Save Signature button). handleSaveSignature
 * is still used by UserProfileForm to save signature via Save and next.
 *
 * File Size Check:
 * - Current: 128
 * - Adding: ~-5
 * - Projected: ~123
 * - Decision: Proceed
 *
 * Related Files:
 * - client/src/components/setup/user-profile/UserProfileForm.tsx
 * - client/src/components/setup/user-profile/components/BroadcastSignature.tsx
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 111 lines
 * - Adding: ~4 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    USER_FORM_HOOK_ADD_USER_LOCATION
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Add userCity, userState, userCountry to form initial values and data sync.
 * These are USER PERSONAL location fields (separate from company address).
 * userCountry uses dropdown selection (not free text).
 *
 * File Size Check:
 * - Current: 77 lines
 * - Adding: ~6-10 lines
 * - Decision: Proceed (within 100-line limit)
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/hooks/useUserForm.ts
 * - client/src/components/setup/user-profile/utils/userValidation.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SPLIT_USER_PROFILE_HOOK_REFINED
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Refined UserProfileForm hook to meet line limits by extracting sync logic.
 * Fixed incorrect relative import paths and type errors.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/hooks/useUserForm.ts
 */
import { useState, useRef, useCallback } from "react";
import { useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSetup } from "../../../Setup";
import { notify } from "../../../../utils/notifications";
import { normalizeOptionalFields, USER_OPTIONAL_FIELDS } from "../../../../utils/formHelpers";
import { userValidationSchema } from "../utils/userValidation";
import { useUserSync } from "./useUserSync";
import { User } from "../../../../interfaces/User";
import { Currency } from "../../../../interfaces/Currency";
// [2026-02-13] CURRENCY_PHASE2: Import inventory service for currency change flow
import { getMyInventoryCount, deleteAllMyInventory } from "../../../../services/inventoryService";

export const useUserForm = () => {
  const { profile, loading, save, setDirty } = useSetup();
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isSignatureDirty, setIsSignatureDirty] = useState(false);
  const nextSectionRef = useRef<string | null>(null);

  // [2026-02-13] CURRENCY_PHASE2: Track original currency + confirmation state
  const originalCurrencyRef = useRef<Currency>("USD");
  const [showCurrencyConfirm, setShowCurrencyConfirm] = useState(false);
  const [pendingInventoryCount, setPendingInventoryCount] = useState(0);
  const [pendingSubmitData, setPendingSubmitData] = useState<Partial<User> | null>(null);

  const formik = useFormik({
    initialValues: {
      firstName: "", lastName: "", jobTitle: "", email: "", phone: "",
      // [2025-12-29] Figma alignment: User personal location (separate from company address)
      userCity: "",
      userState: "",
      userCountry: "",
      currency: "USD" as Currency,
      bio: "", linkedIn: "", x: "", instagram: "", facebook: "", profileImages: null as any, broadcastSignatureHtml: "",
    },
    validationSchema: userValidationSchema,
    onSubmit: async (values) => {
      // [2025-12-29] Bug fix: normalize optional fields before saving
      const submitData = normalizeOptionalFields({
        ...values, tel: values.phone, preferredCurrency: values.currency,
        userCity: values.userCity,
        userState: values.userState,
        userCountry: values.userCountry,
        broadcastSignatureHtml: broadcastSignatureHtml || "",
      }, USER_OPTIONAL_FIELDS);

      // [2026-02-13] CURRENCY_PHASE2: Check if currency changed & user has inventory
      const newCurrency = values.currency;
      if (newCurrency !== originalCurrencyRef.current) {
        try {
          const { data } = await getMyInventoryCount();
          if (data.count > 0) {
            setPendingSubmitData(submitData as Partial<User>);
            setPendingInventoryCount(data.count);
            setShowCurrencyConfirm(true);
            return; // Don't save yet — wait for user confirmation
          }
        } catch { /* count failed — proceed with save */ }
      }

      await doSave(submitData as Partial<User>);
    },
  });

  // [2026-02-13] CURRENCY_PHASE2: Extracted save logic so confirmation can reuse it
  const doSave = useCallback(async (submitData: Partial<User>) => {
    try {
      await save(submitData);
      originalCurrencyRef.current = (submitData as any).preferredCurrency || originalCurrencyRef.current;
      notify.success("Profile updated successfully");
      setDirty(false);
      if (nextSectionRef.current === "reviewuser") navigate("/setup/reviewuser");
      else if (nextSectionRef.current) setSearchParams({ section: nextSectionRef.current });
    } catch (error) {
      notify.error(error, "We couldn't update your profile. Please try again");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save, setDirty, navigate, setSearchParams]);

  // [2026-02-13] CURRENCY_PHASE2: User confirmed — delete inventory then save
  const confirmCurrencyChange = useCallback(async () => {
    if (!pendingSubmitData) return;
    try {
      await deleteAllMyInventory();
      await doSave(pendingSubmitData);
    } catch (error) {
      notify.error(error, "Failed to update currency. Please try again");
    } finally {
      setShowCurrencyConfirm(false);
      setPendingSubmitData(null);
      setPendingInventoryCount(0);
    }
  }, [pendingSubmitData, doSave]);

  // [2026-02-13] CURRENCY_PHASE2: User cancelled — revert currency dropdown
  const cancelCurrencyChange = useCallback(() => {
    formik.setFieldValue("currency", originalCurrencyRef.current);
    setShowCurrencyConfirm(false);
    setPendingSubmitData(null);
    setPendingInventoryCount(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { dbPresence, broadcastSignatureHtml, setBroadcastSignatureHtml } = useUserSync(loading, profile, formik, setDirty, originalCurrencyRef);

  return {
    formik, profile, loading, broadcastSignatureHtml, setBroadcastSignatureHtml,
    isSignatureDirty, setIsSignatureDirty,
    dbPresence, nextSectionRef,
    // [2026-02-13] CURRENCY_PHASE2: Currency change confirmation exports
    showCurrencyConfirm, confirmCurrencyChange, cancelCurrencyChange,
    pendingInventoryCount, originalCurrencyRef,
    // [2026-02-10] MERGE_SAVE_SIGNATURE_INTO_SAVE_NEXT: used by parent to save signature on Save and next
    handleSaveSignature: async (providePayload?: () => Promise<Partial<User>>) => {
      try {
        const payload = providePayload ? await providePayload() : { broadcastSignatureHtml };
        await save(payload as Partial<User>);
        setIsSignatureDirty(false);
      } catch (e) { console.error(e); }
    }
  };
};